import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut, isSuperUser, isEditor } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'content' | 'stats' | 'people' | 'videos' | null

  // Reordered navigation items based on requirements
  const getNavigationItems = () => {
    const baseItems = [];
    
    // Check if user is a student (user role, not admin/editor)
    const isStudent = user && userProfile?.role === 'user';
    
    if (isStudent) {
      // Student navigation - links to discovery pages
      baseItems?.push({
        label: 'Stage',
        path: '/the-stage-student-dashboard',
        icon: 'Home',
        tooltip: 'My Stage dashboard'
      });
      
      baseItems?.push({
        label: 'Musicals',
        path: '/student-musicals-discovery',
        icon: 'Library',
        tooltip: 'Browse musicals'
      });
      
      baseItems?.push({
        label: 'Songs',
        path: '/student-songs-discovery',
        icon: 'Music',
        tooltip: 'Browse songs'
      });
      
      baseItems?.push({
        label: 'Roles',
        path: '/student-roles-discovery',
        icon: 'Users',
        tooltip: 'Browse character roles'
      });

      baseItems?.push({
        label: 'Warm Ups',
        path: '/warm-ups',
        icon: 'Flame',
        tooltip: 'Daily vocal warm up videos'
      });
    } else {
      // Admin/Editor navigation - links to admin pages
      // (Songs, Musicals, Roles are now grouped under Content dropdown)
      // (Talent, Users are now grouped under People dropdown)
      // (Badges is now under Stats dropdown)
      // (Lesson Videos, Warm Ups are now grouped under Videos dropdown)
    }
    
    return baseItems;
  };

  // Content dropdown items (admin only)
  const contentItems = [
    { label: 'Songs', path: '/song-database-list', icon: 'Database', tooltip: 'View analyzed songs' },
    { label: 'Musicals', path: '/all-musicals', icon: 'Library', tooltip: 'Browse all musicals' },
    { label: 'Roles', path: '/all-roles', icon: 'Users', tooltip: 'Browse character roles' },
  ];

  // Stats dropdown items (superuser only)
  const statsItems = [
    { label: 'Tools', path: '/tools-admin-page', icon: 'Settings', tooltip: 'Admin tools for auditions and tips' },
    { label: 'Analytics', path: '/video-engagement-analytics-dashboard', icon: 'BarChart2', tooltip: 'Video engagement analytics' },
    { label: 'Playback', path: '/video-playback-analytics', icon: 'PlayCircle', tooltip: 'Video playback analytics — views, completion & rewatches' },
    { label: 'Badges', path: '/all-badges', icon: 'Award', tooltip: 'Manage achievement badges' },
  ];

  // People dropdown items (superuser only)
  const peopleItems = [
    { label: 'Talent', path: '/talent-management-dashboard', icon: 'Star', tooltip: 'Manage talent profiles' },
    { label: 'Users', path: '/admin', icon: 'UserCheck', tooltip: 'Manage users and permissions' },
  ];

  // Videos dropdown items (superuser only)
  const videosItems = [
    { label: 'Lesson Videos', path: '/all-videos-management', icon: 'Video', tooltip: 'Manage all uploaded videos' },
    { label: 'Warm Ups', path: '/warm-ups-management', icon: 'Flame', tooltip: 'Manage warm up videos for students' },
  ];

  const navigationItems = getNavigationItems();

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const isGroupActive = (items) => {
    return items?.some(item => isActivePath(item?.path));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const handleLogoClick = () => {
    const isStudent = user && userProfile?.role === 'user';
    if (isStudent) {
      navigate('/the-stage-student-dashboard');
    } else {
      navigate('/admin-home-dashboard');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMyAccount = () => {
    setIsUserMenuOpen(false);
    navigate('/universal-account-settings-hub');
  };

  const handleStudentProfile = () => {
    setIsUserMenuOpen(false);
    navigate('/student-profile-editor');
  };

  const handleEducatorProfile = () => {
    setIsUserMenuOpen(false);
    navigate('/educator-profile-management');
  };

  const handleMyStage = () => {
    setIsUserMenuOpen(false);
    const isStudent = user && userProfile?.role === 'user';
    if (isStudent) {
      navigate('/the-stage-student-dashboard');
    } else {
      navigate('/admin-home-dashboard');
    }
  };

  const handleSettingsManagement = () => {
    setIsUserMenuOpen(false);
    navigate('/settings-management');
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    try {
      await signOut();
      navigate('/login-page', { replace: true });
    } catch (error) {
      console.error('Logout navigation error:', error);
      navigate('/login-page', { replace: true });
    }
  };

  const isStudent = user && userProfile?.role === 'user';
  const isAdmin = !isStudent;

  return (
    <header className="bg-background border-b border-border sticky top-0 z-50">
      {/* Main Header */}
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <div className="flex items-center">
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
            aria-label="Go to admin home"
          >
            <img 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo" 
              className="h-8 w-auto object-contain cursor-pointer"
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          <nav className="flex items-center space-x-1">
            {/* Admin: Content dropdown (Songs, Musicals, Roles) */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('content')}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                    ${isGroupActive(contentItems) || openDropdown === 'content' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                  title="Content"
                >
                  <Icon name="BookOpen" size={18} />
                  <span className="font-caption">Content</span>
                  <Icon name={openDropdown === 'content' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
                {openDropdown === 'content' && (
                  <div className="absolute left-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-lg z-50 slide-up-enter">
                    <div className="py-2">
                      {contentItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`
                            w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-200
                            ${isActivePath(item?.path)
                              ? 'text-primary font-semibold bg-muted/50' :'text-popover-foreground hover:bg-muted/50'
                            }
                          `}
                          title={item?.tooltip}
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Admin SuperUser: Stats dropdown (Tools, Analytics, Playback) */}
            {isAdmin && isSuperUser() && (
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('stats')}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                    ${isGroupActive(statsItems) || openDropdown === 'stats' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                  title="Stats"
                >
                  <Icon name="BarChart" size={18} />
                  <span className="font-caption">Stats</span>
                  <Icon name={openDropdown === 'stats' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
                {openDropdown === 'stats' && (
                  <div className="absolute left-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-lg z-50 slide-up-enter">
                    <div className="py-2">
                      {statsItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`
                            w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-200
                            ${isActivePath(item?.path)
                              ? 'text-primary font-semibold bg-muted/50' :'text-popover-foreground hover:bg-muted/50'
                            }
                          `}
                          title={item?.tooltip}
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Admin SuperUser: Videos dropdown (Lesson Videos, Warm Ups) */}
            {isAdmin && isSuperUser() && (
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('videos')}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                    ${isGroupActive(videosItems) || openDropdown === 'videos' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                  title="Videos"
                >
                  <Icon name="Video" size={18} />
                  <span className="font-caption">Videos</span>
                  <Icon name={openDropdown === 'videos' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
                {openDropdown === 'videos' && (
                  <div className="absolute left-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-lg z-50 slide-up-enter">
                    <div className="py-2">
                      {videosItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`
                            w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-200
                            ${isActivePath(item?.path)
                              ? 'text-primary font-semibold bg-muted/50' :'text-popover-foreground hover:bg-muted/50'
                            }
                          `}
                          title={item?.tooltip}
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* People dropdown (superuser only) */}
            {isAdmin && isSuperUser() && (
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('people')}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                    ${isGroupActive(peopleItems) || openDropdown === 'people' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                  title="People"
                >
                  <Icon name="Users" size={18} />
                  <span className="font-caption">People</span>
                  <Icon name={openDropdown === 'people' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
                {openDropdown === 'people' && (
                  <div className="absolute left-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-lg z-50 slide-up-enter">
                    <div className="py-2">
                      {peopleItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`
                            w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-200
                            ${isActivePath(item?.path)
                              ? 'text-primary font-semibold bg-muted/50' :'text-popover-foreground hover:bg-muted/50'
                            }
                          `}
                          title={item?.tooltip}
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Remaining flat nav items (student items or admin items not in dropdowns) */}
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }
                `}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={18} />
                <span className="font-caption">{item?.label}</span>
              </button>
            ))}
          </nav>

          {/* User Menu */}
          {user && (
            <div className="relative ml-4">
              <button
                onClick={toggleUserMenu}
                className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
                aria-label="User menu"
              >
                <Icon name="User" size={24} />
              </button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-xl shadow-lg z-50 slide-up-enter">
                  <div className="py-2">
                    <button
                      onClick={handleMyStage}
                      className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="Home" size={16} />
                      <span>My Stage</span>
                    </button>
                    <button
                      onClick={handleMyAccount}
                      className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="Settings" size={16} />
                      <span>My Account</span>
                    </button>
                    {user && userProfile?.role === 'user' && (
                      <button
                        onClick={handleStudentProfile}
                        className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                      >
                        <Icon name="User" size={16} />
                        <span>Student Profile</span>
                      </button>
                    )}
                    {(userProfile?.role === 'talent' || isSuperUser()) && (
                      <button
                        onClick={handleEducatorProfile}
                        className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                      >
                        <Icon name="Briefcase" size={16} />
                        <span>Educator Profile</span>
                      </button>
                    )}
                    {isSuperUser() && (
                      <button
                        onClick={handleSettingsManagement}
                        className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                      >
                        <Icon name="Sliders" size={16} />
                        <span>Settings Management</span>
                      </button>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted/50 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
          aria-label="Toggle navigation menu"
        >
          <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>
      </div>
      
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-card slide-up-enter">
          <nav className="px-6 py-4 space-y-2">
            {/* Admin: Content group label + items */}
            {isAdmin && (
              <>
                <div className="px-4 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Content</div>
                {contentItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left
                      ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-caption">{item?.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* Admin SuperUser: Stats group label + items */}
            {isAdmin && isSuperUser() && (
              <>
                <div className="px-4 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">Stats</div>
                {statsItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left
                      ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-caption">{item?.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* Admin SuperUser: Videos group label + items */}
            {isAdmin && isSuperUser() && (
              <>
                <div className="px-4 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">Videos</div>
                {videosItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left
                      ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-caption">{item?.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* People dropdown (superuser only) */}
            {isAdmin && isSuperUser() && (
              <>
                <div className="px-4 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">People</div>
                {peopleItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left
                      ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-caption">{item?.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* Remaining flat nav items */}
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }
                `}
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-caption">{item?.label}</span>
              </button>
            ))}
            
            {/* Mobile User Menu */}
            {user && (
              <>
                <div className="border-t border-border my-2"></div>
                <button
                  onClick={handleMyStage}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Icon name="Home" size={20} />
                  <span className="font-caption">My Stage</span>
                </button>
                <button
                  onClick={handleMyAccount}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Icon name="Settings" size={20} />
                  <span className="font-caption">My Account</span>
                </button>
                {user && userProfile?.role === 'user' && (
                  <button
                    onClick={handleStudentProfile}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  >
                    <Icon name="User" size={20} />
                    <span className="font-caption">Student Profile</span>
                  </button>
                )}
                {(userProfile?.role === 'talent' || isSuperUser()) && (
                  <button
                    onClick={handleEducatorProfile}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  >
                    <Icon name="Briefcase" size={20} />
                    <span className="font-caption">Educator Profile</span>
                  </button>
                )}
                {isSuperUser() && (
                  <button
                    onClick={handleSettingsManagement}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  >
                    <Icon name="Sliders" size={20} />
                    <span className="font-caption">Settings Management</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-left text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="font-caption">Log Out</span>
                </button>
              </>
            )}
          </nav>
        </div>
      )}

      {/* Click outside to close dropdowns and user menu */}
      {(isUserMenuOpen || openDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setIsUserMenuOpen(false); setOpenDropdown(null); }}
        />
      )}
    </header>
  );
};

export default Header;